import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import {TriggerActionType} from 'records/TriggerRuleAction';
import './EditActionTypes.styl';
import {Map} from 'immutable';
import RadioGroup from 'components/RadioGroup';
import SquareRadioButton from 'components/SquareRadioButton';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import NotificationThrottleEdit from 'components/triggers/trigger-actions/NotificationThrottleEdit';
import UnsavedTriggerActions from 'actions/triggers/UnsavedTriggerActions';
import UnsavedTriggerStore from 'stores/triggers/UnsavedTriggerStore';
import SquareToggleButton from 'components/SquareToggleButton';
import AccountStore from 'stores/AccountStore';
import AccountActions from 'actions/AccountActions';
import {throttleOptions} from 'components/triggers/trigger-actions/ThrottleOptions';
import {InboxIcon} from '@components/icon/InboxIcon';
import {EmailIcon} from '@components/icon/EmailIcon';
import {SlackIcon} from '@components/icon/SlackIcon';
import {BellOnIcon} from '@components/icon/BellOnIcon';
import {MergeIcon} from '@components/icon/MergeIcon';

const throttleScale = Map(throttleOptions.map(item => [item.value, item.scale]));

const ThrottleType = Object.freeze({
  NONE: 'none',
  THROTTLED: 'throttled',
});

@connectToStores
export default class EditActionTypes extends React.PureComponent {
  constructor(props) {
    super(props);
    this.setDamping = this.setDamping.bind(this);
    this.toggleThrottle = this.toggleThrottle.bind(this);
    this.state = {
      throttleType: props.rule.dampingDelay ? ThrottleType.THROTTLED : ThrottleType.NONE,
    };
  }

  static getStores() {
    return [UnsavedTriggerStore, AccountStore];
  }

  static getPropsFromStores() {
    const {damping, sendTo, actionType} = UnsavedTriggerStore.getState();
    const {hasSlack, hasSlackLoaded} = AccountStore.getState();
    return {
      damping,
      sendToEmail: sendTo.get(TriggerActionType.EMAIL),
      sendToInbox: sendTo.get(TriggerActionType.FOLDER),
      sendToSlack: sendTo.get(TriggerActionType.SLACK),
      actionType,
      hasSlack,
      hasSlackLoaded,
    };
  }

  getActive(type) {
    const action = this.props.actions.find(action => action.configuration.type === type);
    return !!(action && action.active);
  }

  componentDidMount() {
    const {rule, actions} = this.props;

    AccountActions.hasSlack.defer();

    if (actions) {
      const emailActive = this.getActive(TriggerActionType.EMAIL);
      const inboxActive = this.getActive(TriggerActionType.FOLDER);
      const slackActive = this.getActive(TriggerActionType.SLACK);

      UnsavedTriggerActions.setSendToEmail.defer(emailActive);
      UnsavedTriggerActions.setSendToInbox.defer(inboxActive);
      UnsavedTriggerActions.setSendToSlack.defer(slackActive);
    }

    UnsavedTriggerActions.setDamping.defer(rule.dampingDelay);
  }

  setDamping(throttleValue, throttlePeriod) {
    const scale = throttleScale.get(throttlePeriod);
    const damping = scale * throttleValue;
    UnsavedTriggerActions.setDamping(damping);
  }

  getThrottleConfig() {
    const {damping} = this.props;

    // If damping is 0 don't bother rendering these controls
    if (damping === 0) return {};

    const throttleType = throttleOptions.findLast(item => damping % item.scale === 0);
    if (!throttleType) {
      console.error(
        `Couldn't match damping: ${damping} with throttle type. Falling back to default`
      );
      return {
        period: 'week',
        value: 1,
      };
    }

    return {
      value: damping / throttleType.scale,
      period: throttleType.value,
    };
  }

  toggleThrottle(throttleType) {
    if (throttleType === ThrottleType.NONE) {
      UnsavedTriggerActions.setDamping(0);
    } else {
      this.setDamping(1, 'week');
    }
    this.setState({throttleType});
  }

  render() {
    const {throttleType} = this.state;
    const {actionType, sendToEmail, sendToInbox, sendToSlack, hasSlack, hasSlackLoaded} =
      this.props;
    const {value, period} = this.getThrottleConfig();
    const showThrottleSettings = actionType !== 'trend';

    const showSlack = hasSlack && hasSlackLoaded;
    return (
      <div className="edit-action-types">
        <div>
          <FieldContainer label="How to send">
            <div className={`notification-method-buttons ${showSlack && 'hasSlack'}`}>
              <SquareToggleButton
                icon={InboxIcon}
                text="Send to EV Inbox"
                value={sendToInbox}
                onToggle={() => UnsavedTriggerActions.setSendToInbox(!sendToInbox)}
                vertical
              />
              <SquareToggleButton
                icon={EmailIcon}
                text="Send to Email"
                value={sendToEmail}
                onToggle={() => UnsavedTriggerActions.setSendToEmail(!sendToEmail)}
                vertical
              />
              {showSlack && (
                <SquareToggleButton
                  icon={SlackIcon}
                  text="Send to Slack"
                  value={sendToSlack}
                  onToggle={() => UnsavedTriggerActions.setSendToSlack(!sendToSlack)}
                  vertical
                />
              )}
            </div>
          </FieldContainer>
          {showThrottleSettings && (
            <FieldContainer label="How often">
              <RadioGroup value={throttleType} onSelect={this.toggleThrottle}>
                <SquareRadioButton
                  icon={BellOnIcon}
                  value={ThrottleType.NONE}
                  text="Send notifications whenever the event occurs"
                  vertical
                />
                <SquareRadioButton
                  icon={MergeIcon}
                  value={ThrottleType.THROTTLED}
                  text="Combine into a single notification when this event matches repeatedly"
                  vertical
                />
              </RadioGroup>
            </FieldContainer>
          )}
          {throttleType === ThrottleType.THROTTLED && (
            <FieldContainer label=" ">
              <NotificationThrottleEdit
                value={value}
                period={period}
                setDamping={this.setDamping}
              />
            </FieldContainer>
          )}
        </div>
      </div>
    );
  }
}
